
import React, { useState } from 'react';
import { Lock, ShieldCheck, ArrowRight, Loader2, AlertCircle, X, Palmtree } from 'lucide-react';
import { CONFIG } from '../config';

interface AdminLoginProps {
  onSuccess: () => void;
  onCancel: () => void;
}

const AdminLogin: React.FC<AdminLoginProps> = ({ onSuccess, onCancel }) => {
  const [passcode, setPasscode] = useState('');
  const [error, setError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(false);

    // Simulated verification delay for security feel
    setTimeout(() => {
      if (passcode === CONFIG.ADMIN_PASSCODE) {
        onSuccess();
      } else {
        setError(true);
        setPasscode('');
      }
      setIsLoading(false);
    }, 800);
  };

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center p-6 overflow-hidden">
      {/* Immersive Background */}
      <div className="absolute inset-0 bg-slate-950">
        <img 
          src="https://images.unsplash.com/photo-1544551763-46a013bb70d5?q=80&w=2000" 
          className="w-full h-full object-cover opacity-20 scale-110 blur-sm"
          alt="Login Background" 
        />
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-slate-950/80 to-sky-900/20"></div>
      </div>

      <div className="relative w-full max-w-md animate-in zoom-in-95 fade-in duration-500">
        <div className="glass-card rounded-[3rem] p-10 md:p-14 border-white/10 shadow-[0_32px_120px_-16px_rgba(0,0,0,0.5)]">
          <button 
            onClick={onCancel}
            className="absolute top-8 right-8 p-2 text-white/50 hover:text-white transition-colors"
          >
            <X className="w-6 h-6" />
          </button>

          <div className="flex flex-col items-center text-center mb-12">
            <div className="w-20 h-20 bg-sky-500 rounded-3xl flex items-center justify-center mb-8 shadow-2xl shadow-sky-500/20">
              <ShieldCheck className="w-10 h-10 text-white" />
            </div>
            <span className="text-[10px] font-bold text-sky-400 uppercase tracking-wider mb-4">Security Protocol</span>
            <h2 className="text-3xl font-bold text-white mb-4">Staff Verification</h2>
            <p className="text-white/70 text-sm leading-relaxed max-w-xs font-normal">
              This terminal is reserved for Balut Island authorized personnel only.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-8">
            <div className="space-y-3">
              <div className={`relative transition-all duration-300 ${error ? 'translate-x-1' : ''}`}>
                <input 
                  type="password"
                  autoFocus
                  value={passcode}
                  onChange={(e) => setPasscode(e.target.value)}
                  placeholder="Enter Access Key"
                  className={`w-full bg-white/10 border ${error ? 'border-red-500/50' : 'border-white/20'} rounded-2xl py-5 px-8 text-white text-center text-lg tracking-[0.5em] focus:outline-none focus:border-sky-500/50 transition-all placeholder:text-white/30 placeholder:tracking-normal placeholder:text-sm font-sans shadow-inner`}
                />
                <Lock className={`absolute left-6 top-1/2 -translate-y-1/2 w-4 h-4 transition-colors ${error ? 'text-red-500' : 'text-white/40'}`} />
              </div>
              {error && (
                <div className="flex items-center justify-center gap-2 text-red-500 text-[10px] font-bold uppercase tracking-widest animate-in fade-in slide-in-from-top-1">
                  <AlertCircle className="w-3 h-3" /> Unauthorized Access Attempt
                </div>
              )}
            </div>

            <button 
              disabled={isLoading || !passcode}
              className="group w-full py-6 bg-white text-slate-950 rounded-2xl font-extrabold text-[11px] uppercase tracking-wider flex items-center justify-center gap-4 hover:bg-sky-500 hover:text-white transition-all shadow-2xl active:scale-[0.98] disabled:opacity-20 disabled:grayscale"
            >
              {isLoading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>Authorize Terminal <ArrowRight className="w-5 h-5 group-hover:translate-x-1.5 transition-transform" /></>
              )}
            </button>
          </form>

          <div className="mt-16 pt-8 border-t border-white/10 flex flex-col items-center gap-6">
            <div className="flex items-center gap-3 opacity-40">
              <Palmtree className="w-4 h-4 text-white" />
              <span className="text-[9px] font-bold text-white uppercase tracking-wider">Balut Island Portal</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
